import Vue from "vue";
import { ActionTree, MutationTree, GetterTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import difference from "lodash/difference";
import findIndex from "lodash/findIndex";
import cloneDeep from "lodash/cloneDeep";
import { saveAs } from "file-saver";
import {
  buildRoutingSheetWorkbookBuffer,
  RoutingSheetBatchPayload,
} from "@/modules/DeliverySheetBoard/routingSheetExcel";

interface DeliverySheetBoardState {
  filterDataLoading: boolean;
  filterDataLoaded: boolean;
  cities: any[];

  cityId: string | null;
  deliveryDate: string | null;
  by: string;

  dataRequested: boolean;
  dataLoading: boolean;
  dataLoaded: boolean;
  dataRefreshing: boolean;
  refreshRequestId: number;

  products: any[];
  variants: any[];

  vehicles: any[];
  zones: any[];
  computedZones: any[];

  valid: boolean;

  savingData: boolean;
}

type DsLogLevel = "debug" | "info" | "warn" | "error";

const DS_LOG_LEVELS: Record<DsLogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function dsLog(level: DsLogLevel, message: string, data?: any) {
  const configured = (
    process.env.VUE_APP_LOG_LEVEL ||
    process.env.LOG_LEVEL ||
    "debug"
  ).toLowerCase() as DsLogLevel;
  const minLevel = DS_LOG_LEVELS[configured] ?? DS_LOG_LEVELS.debug;
  if (DS_LOG_LEVELS[level] < minLevel) {
    return;
  }
  let fn: (...args: any[]) => void = console.log;
  if (level === "error") {
    fn = console.error;
  } else if (level === "warn") {
    fn = console.warn;
  }
  if (data !== undefined) {
    fn(message, data);
  } else {
    fn(message);
  }
}

const state: DeliverySheetBoardState = {
  filterDataLoading: false,
  filterDataLoaded: false,
  cities: [],

  cityId: null,
  deliveryDate: null,
  by: "delivery",

  products: [],
  variants: [],

  dataRequested: false,
  dataLoading: false,
  dataLoaded: false,
  dataRefreshing: false,
  refreshRequestId: 0,

  vehicles: [],
  zones: [],
  computedZones: [],

  valid: false,

  savingData: false,
};

const LOAD_CITIES_QUERY = "LOAD_CITIES_QUERY";
const LOAD_DELIVERY_ZONES_DATA_QUERY = "LOAD_DELIVERY_ZONES_DATA_QUERY";
const LOAD_TARGET_DEALS_QUERY = "LOAD_TARGET_DEALS_QUERY";
const LOAD_VEHICLES_QUERY = "LOAD_VEHICLES_QUERY";
const PL_PRODUCTS_LIST = "PL_PRODUCTS_LIST";
const PL_PRODUCT_VARIANTS_LIST = "PL_PRODUCT_VARIANTS_LIST";
const ROUTING_SHEET_DATA = "ROUTING_SHEET_DATA";

function buildRoutingSheetPrintQueries(instanceIds: any[], by: string) {
  const point_field =
    by == "destination" ? "destination_delivery_point_id" : "delivery_point_id";
  return {
    delivery_instance: {
      columns: {
        id: "delivery_instances.id",
        driver_name: "drivers.name",
        vehicle_number: "vehicles.number",
        delivery_date: "delivery_instances.delivery_date",
        loading_time: "delivery_instances.loading_time",
      },
      from: "s:delivery_instances",
      from_as: "delivery_instances",
      joins: [
        {
          join: "s:employees",
          type: "left",
          as: "drivers",
          on: {
            $eq: ["drivers.id", "delivery_instances.driver_id"],
          },
        },
        {
          join: "s:delivery_instance_deals",
          as: "delivery_instance_deals",
          on: {
            $eq: [
              "delivery_instance_deals.delivery_instance_id",
              "delivery_instances.id",
            ],
          },
        },
        {
          join: "s:deals",
          as: "deal",
          on: {
            $eq: ["delivery_instance_deals.deal_id", "deal.id"],
          },
        },
        {
          join: "s:vehicles",
          type: "left",
          as: "vehicles",
          on: {
            $eq: ["vehicles.id", "delivery_instances.vehicle_id"],
          },
        },
      ],
      where: {
        $and: [
          { $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:3" }] }] },
          {
            $in: [
              "delivery_instances.id",
              {
                $: instanceIds,
              },
            ],
          },
          //   { $in: ['statuses.code', {$: ['formation', 'delivery'] }]}
        ],
      },
      groups: [
        "delivery_instances.id",
        "drivers.name",
        "vehicles.number",
        "delivery_instances.delivery_date",
        "delivery_instances.loading_time",
      ],
    },
    deals: {
      columns: {
        id: "deal.id",
        type_id: "deal.type_id",
        take: { $eq: ["deal.type_id", { $: "d:deals_types:2" }] },
        not_take: {
          $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:2" }] }],
        },
        client_name: "client.name",
        special_orders: "client.special_orders",
        manager_name: "manager.name",
        expected_delivery_date: "deal.expected_delivery_date",
        confirmed_at: "deal.confirmed_at",
        point_address: "point.address",
        point_comment: "point.comment",
        payment_type_name: "ptype.name",
        product_name: "p.name",
        product_variant_name: "pc.name",
        b_quantity: "dcp.container_quantity",
        weight: {
          $case: [
            ["pc.price_per_kg", "dcp.container_weight"],
            [
              {
                $: true,
              },
              {
                $mul: ["pc.weight", "dcp.container_quantity"],
              },
            ],
          ],
        },
        b_price: "pc.price",
        special_price: "personal_settings.container_price",
        cost: "dcp.container_price",
        notes: "dcp.notes",
        color: {
          $case: [
            [
              { $in: ["client.type_id", { $: ["d:client_types:6"] }] },
              { $: "#CCCCCC" },
            ],
            [
              {
                $: true,
              },
              "client.color",
            ],
          ],
        },
      },
      from: "s:deals",
      from_as: "deal",
      joins: [
        {
          join: "s:statuses",
          type: "left",
          as: "statuses",
          on: {
            $eq: ["deal.status_id", "statuses.id"],
          },
        },
        {
          join: "s:delivery_instance_deals",
          as: "delivery_instance_deals",
          on: {
            $eq: ["delivery_instance_deals.deal_id", "deal.id"],
          },
        },
        {
          join: "s:employees",
          type: "left",
          as: "manager",
          on: {
            $eq: ["manager.id", "deal.manager_id"],
          },
        },
        {
          join: "s:clients",
          type: "left",
          as: "client",
          on: {
            $eq: ["client.id", "deal.client_id"],
          },
        },
        {
          join: "s:payment_deferment_periods",
          type: "left",
          as: "pdp",
          on: {
            $eq: ["pdp.id", "client.payment_deferment_period_id"],
          },
        },
        {
          join: "s:payment_types",
          type: "left",
          as: "ptype",
          on: {
            $eq: ["deal.payment_type_id", "ptype.id"],
          },
        },
        {
          join: "s:delivery_points",
          type: "left",
          as: "point",
          on: {
            $eq: ["deal." + point_field, "point.id"],
          },
        },
        {
          join: "s:cities",
          type: "left",
          as: "city",
          on: {
            $eq: ["point.city_id", "city.id"],
          },
        },
        {
          join: "s:warehouses",
          type: "left",
          as: "warehouse",
          on: {
            $eq: ["deal.delivery_warehouse_id", "warehouse.id"],
          },
        },
        {
          join: "s:deal_product_containers",
          type: "left",
          as: "dcp",
          on: {
            $eq: ["dcp.deal_id", "deal.id"],
          },
        },
        {
          join: "s:product_containers",
          type: "left",
          as: "pc",
          on: {
            $eq: ["pc.id", "dcp.product_container_id"],
          },
        },
        {
          join: "s:products",
          type: "left",
          as: "p",
          on: {
            $eq: ["p.id", "pc.product_id"],
          },
        },
        {
          join: "s:client_product_containers",
          type: "left",
          as: "personal_settings",
          on: {
            $and: [
              {
                $eq: ["personal_settings.product_container_id", "pc.id"],
              },
              {
                $eq: ["personal_settings.client_id", "client.id"],
              },
            ],
          },
        },
      ],
      order: [
        {
          $asc: ["delivery_instance_deals.position"],
        },
      ],
      where: {
        $and: [
          {
            $in: [
              "delivery_instance_deals.delivery_instance_id",
              {
                $: instanceIds,
              },
            ],
          },
          { $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:3" }] }] },
          { $in: ["statuses.code", { $: ["formation", "delivery"] }] },
        ],
      },
    },
    deals_sum: {
      columns: {
        total_weight: {
          $sum: {
            $mul: ["pc.weight", "dcp.container_quantity"],
          },
        },
        total_sum: {
          $sum: "dcp.container_price",
        },
        total_quantity: {
          $sum: "dcp.container_quantity",
        },
      },
      from: "s:deals",
      from_as: "deal",
      joins: [
        {
          join: "s:statuses",
          type: "left",
          as: "statuses",
          on: {
            $eq: ["deal.status_id", "statuses.id"],
          },
        },
        {
          join: "s:delivery_instance_deals",
          as: "delivery_instance_deals",
          on: {
            $eq: ["delivery_instance_deals.deal_id", "deal.id"],
          },
        },
        {
          join: "s:employees",
          type: "left",
          as: "manager",
          on: {
            $eq: ["manager.id", "deal.manager_id"],
          },
        },
        {
          join: "s:clients",
          type: "left",
          as: "client",
          on: {
            $eq: ["client.id", "deal.client_id"],
          },
        },
        {
          join: "s:payment_deferment_periods",
          type: "left",
          as: "pdp",
          on: {
            $eq: ["pdp.id", "client.payment_deferment_period_id"],
          },
        },
        {
          join: "s:payment_types",
          type: "left",
          as: "ptype",
          on: {
            $eq: ["deal.payment_type_id", "ptype.id"],
          },
        },
        {
          join: "s:delivery_points",
          type: "left",
          as: "point",
          on: {
            $eq: ["deal." + point_field, "point.id"],
          },
        },
        {
          join: "s:cities",
          type: "left",
          as: "city",
          on: {
            $eq: ["point.city_id", "city.id"],
          },
        },
        {
          join: "s:deal_product_containers",
          type: "left",
          as: "dcp",
          on: {
            $eq: ["dcp.deal_id", "deal.id"],
          },
        },
        {
          join: "s:product_containers",
          type: "left",
          as: "pc",
          on: {
            $eq: ["pc.id", "dcp.product_container_id"],
          },
        },
        {
          join: "s:products",
          type: "left",
          as: "p",
          on: {
            $eq: ["p.id", "pc.product_id"],
          },
        },
      ],
      where: {
        $and: [
          { $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:3" }] }] },
          {
            $in: [
              "delivery_instance_deals.delivery_instance_id",
              {
                $: instanceIds,
              },
            ],
          },
          { $in: ["statuses.code", { $: ["formation", "delivery"] }] },
        ],
      },
    },
    products: {
      columns: {
        product_id: "containers.product_id",
        containers: {
          $array_agg: {
            $obj: {
              product_name: "containers.product_name",
              container_name: "containers.name",
              container_count: "containers.count",
              container_weight: "containers.weight",
            },
          },
        },
        container_count_sum: {
          $sum: "containers.count",
        },
        container_weight_sum: {
          $sum: "containers.weight",
        },
      },
      from: {
        columns: {
          product_id: "p.id",
          product_name: "p.name",
          name: "pc.name",
          count: {
            $sum: "dpc.container_quantity",
          },
          weight: {
            $sum: {
              $case: [
                ["pc.price_per_kg", "dpc.container_weight"],
                [
                  {
                    $: true,
                  },
                  {
                    $mul: ["pc.weight", "dpc.container_quantity"],
                  },
                ],
              ],
            },
          },
        },
        from: "s:deal_product_containers",
        from_as: "dpc",
        joins: [
          {
            join: "s:product_containers",
            as: "pc",
            on: {
              $eq: ["pc.id", "dpc.product_container_id"],
            },
          },
          {
            join: "s:products",
            as: "p",
            on: {
              $eq: ["p.id", "pc.product_id"],
            },
          },
        ],
        where: {
          $in: [
            "dpc.deal_id",
            {
              columns: {
                id: "deal.id",
              },
              from: "s:deals",
              from_as: "deal",
              joins: [
                {
                  join: "s:statuses",
                  type: "left",
                  as: "statuses",
                  on: {
                    $eq: ["deal.status_id", "statuses.id"],
                  },
                },
                {
                  join: "s:delivery_instance_deals",
                  as: "delivery_instance_deals",
                  on: {
                    $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                  },
                },
                {
                  join: "s:delivery_points",
                  type: "left",
                  as: "point",
                  on: {
                    $eq: ["deal." + point_field, "point.id"],
                  },
                },
                {
                  join: "s:cities",
                  type: "left",
                  as: "city",
                  on: {
                    $eq: ["point.city_id", "city.id"],
                  },
                },
              ],
              where: {
                $and: [
                  {
                    $in: [
                      "delivery_instance_deals.delivery_instance_id",
                      {
                        $: instanceIds,
                      },
                    ],
                  },
                  {
                    $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:3" }] }],
                  },
                  { $in: ["statuses.code", { $: ["formation", "delivery"] }] },
                ],
              },
            },
          ],
        },
        groups: ["p.id", "p.name", "pc.id", "pc.name"],
      },
      from_as: "containers",
      groups: ["containers.product_id", "containers.product_name"],
    },
    payments: {
      columns: {
        type_name: "pt.name",
        amount: {
          $sum: "dpc.container_price",
        },
      },
      from: "s:deal_product_containers",
      from_as: "dpc",
      joins: [
        {
          join: "s:deals",
          as: "d",
          on: {
            $eq: ["d.id", "dpc.deal_id"],
          },
        },
        {
          join: "s:payment_types",
          as: "pt",
          type: "left",
          on: {
            $eq: ["pt.id", "d.payment_type_id"],
          },
        },
      ],
      where: {
        $in: [
          "dpc.deal_id",
          {
            columns: {
              id: "deal.id",
            },
            from: "s:deals",
            from_as: "deal",
            joins: [
              {
                join: "s:statuses",
                type: "left",
                as: "statuses",
                on: {
                  $eq: ["deal.status_id", "statuses.id"],
                },
              },
              {
                join: "s:delivery_instance_deals",
                as: "delivery_instance_deals",
                on: {
                  $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                },
              },
              {
                join: "s:delivery_points",
                type: "left",
                as: "point",
                on: {
                  $eq: ["deal." + point_field, "point.id"],
                },
              },
              {
                join: "s:cities",
                type: "left",
                as: "city",
                on: {
                  $eq: ["point.city_id", "city.id"],
                },
              },
            ],
            where: {
              $and: [
                {
                  $in: [
                    "delivery_instance_deals.delivery_instance_id",
                    {
                      $: instanceIds,
                    },
                  ],
                },
                { $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:3" }] }] },
                { $in: ["statuses.code", { $: ["formation", "delivery"] }] },
              ],
            },
          },
        ],
      },
      groups: ["pt.id", "pt.name"],
    },
    delivery_instance_return: {
      columns: {
        id: "delivery_instances.id",
        driver_name: "drivers.name",
        vehicle_number: "vehicles.number",
        delivery_date: "delivery_instances.delivery_date",
        loading_time: "delivery_instances.loading_time",
      },
      from: "s:delivery_instances",
      from_as: "delivery_instances",
      joins: [
        {
          join: "s:employees",
          type: "left",
          as: "drivers",
          on: {
            $eq: ["drivers.id", "delivery_instances.driver_id"],
          },
        },
        {
          join: "s:delivery_instance_deals",
          as: "delivery_instance_deals",
          on: {
            $eq: [
              "delivery_instance_deals.delivery_instance_id",
              "delivery_instances.id",
            ],
          },
        },
        {
          join: "s:deals",
          as: "deal",
          on: {
            $eq: ["delivery_instance_deals.deal_id", "deal.id"],
          },
        },
        {
          join: "s:vehicles",
          type: "left",
          as: "vehicles",
          on: {
            $eq: ["vehicles.id", "delivery_instances.vehicle_id"],
          },
        },
      ],
      where: {
        $and: [
          { $eq: ["deal.type_id", { $: "d:deals_types:3" }] },
          {
            $in: [
              "delivery_instances.id",
              {
                $: instanceIds,
              },
            ],
          },
          //   { $in: ['statuses.code', {$: ['formation', 'delivery'] }]}
        ],
      },
      groups: [
        "delivery_instances.id",
        "drivers.name",
        "vehicles.number",
        "delivery_instances.delivery_date",
        "delivery_instances.loading_time",
      ],
    },
    deals_return: {
      columns: {
        id: "deal.id",
        type_id: "deal.type_id",
        take: { $eq: ["deal.type_id", { $: "d:deals_types:2" }] },
        not_take: {
          $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:2" }] }],
        },
        client_name: "client.name",
        special_orders: "client.special_orders",
        manager_name: "manager.name",
        expected_delivery_date: "deal.expected_delivery_date",
        confirmed_at: "deal.confirmed_at",
        point_address: "point.address",
        point_comment: "point.comment",
        payment_type_name: "ptype.name",
        product_name: "p.name",
        product_variant_name: "pc.name",
        b_quantity: "dcp.container_quantity",
        weight: {
          $case: [
            ["pc.price_per_kg", "dcp.container_weight"],
            [
              {
                $: true,
              },
              {
                $mul: ["pc.weight", "dcp.container_quantity"],
              },
            ],
          ],
        },
        b_price: "pc.price",
        special_price: "personal_settings.container_price",
        cost: "dcp.container_price",
        notes: "dcp.notes",
        color: {
          $case: [
            [
              { $in: ["client.type_id", { $: ["d:client_types:6"] }] },
              { $: "#CCCCCC" },
            ],
            [
              {
                $: true,
              },
              "client.color",
            ],
          ],
        },
      },
      from: "s:deals",
      from_as: "deal",
      joins: [
        {
          join: "s:statuses",
          type: "left",
          as: "statuses",
          on: {
            $eq: ["deal.status_id", "statuses.id"],
          },
        },
        {
          join: "s:delivery_instance_deals",
          as: "delivery_instance_deals",
          on: {
            $eq: ["delivery_instance_deals.deal_id", "deal.id"],
          },
        },
        {
          join: "s:employees",
          type: "left",
          as: "manager",
          on: {
            $eq: ["manager.id", "deal.manager_id"],
          },
        },
        {
          join: "s:clients",
          type: "left",
          as: "client",
          on: {
            $eq: ["client.id", "deal.client_id"],
          },
        },
        {
          join: "s:payment_deferment_periods",
          type: "left",
          as: "pdp",
          on: {
            $eq: ["pdp.id", "client.payment_deferment_period_id"],
          },
        },
        {
          join: "s:payment_types",
          type: "left",
          as: "ptype",
          on: {
            $eq: ["deal.payment_type_id", "ptype.id"],
          },
        },
        {
          join: "s:delivery_points",
          type: "left",
          as: "point",
          on: {
            $eq: ["deal." + point_field, "point.id"],
          },
        },
        {
          join: "s:cities",
          type: "left",
          as: "city",
          on: {
            $eq: ["point.city_id", "city.id"],
          },
        },
        {
          join: "s:warehouses",
          type: "left",
          as: "warehouse",
          on: {
            $eq: ["deal.delivery_warehouse_id", "warehouse.id"],
          },
        },
        {
          join: "s:deal_product_containers",
          type: "left",
          as: "dcp",
          on: {
            $eq: ["dcp.deal_id", "deal.id"],
          },
        },
        {
          join: "s:product_containers",
          type: "left",
          as: "pc",
          on: {
            $eq: ["pc.id", "dcp.product_container_id"],
          },
        },
        {
          join: "s:products",
          type: "left",
          as: "p",
          on: {
            $eq: ["p.id", "pc.product_id"],
          },
        },
        {
          join: "s:client_product_containers",
          type: "left",
          as: "personal_settings",
          on: {
            $and: [
              {
                $eq: ["personal_settings.product_container_id", "pc.id"],
              },
              {
                $eq: ["personal_settings.client_id", "client.id"],
              },
            ],
          },
        },
      ],
      order: [
        {
          $asc: ["delivery_instance_deals.position"],
        },
      ],
      where: {
        $and: [
          {
            $in: [
              "delivery_instance_deals.delivery_instance_id",
              {
                $: instanceIds,
              },
            ],
          },
          { $eq: ["deal.type_id", { $: "d:deals_types:3" }] },
          { $in: ["statuses.code", { $: ["formation", "delivery"] }] },
        ],
      },
    },
    deals_sum_return: {
      columns: {
        total_weight: {
          $sum: {
            $mul: ["pc.weight", "dcp.container_quantity"],
          },
        },
        total_sum: {
          $sum: "dcp.container_price",
        },
        total_quantity: {
          $sum: "dcp.container_quantity",
        },
      },
      from: "s:deals",
      from_as: "deal",
      joins: [
        {
          join: "s:statuses",
          type: "left",
          as: "statuses",
          on: {
            $eq: ["deal.status_id", "statuses.id"],
          },
        },
        {
          join: "s:delivery_instance_deals",
          as: "delivery_instance_deals",
          on: {
            $eq: ["delivery_instance_deals.deal_id", "deal.id"],
          },
        },
        {
          join: "s:employees",
          type: "left",
          as: "manager",
          on: {
            $eq: ["manager.id", "deal.manager_id"],
          },
        },
        {
          join: "s:clients",
          type: "left",
          as: "client",
          on: {
            $eq: ["client.id", "deal.client_id"],
          },
        },
        {
          join: "s:payment_deferment_periods",
          type: "left",
          as: "pdp",
          on: {
            $eq: ["pdp.id", "client.payment_deferment_period_id"],
          },
        },
        {
          join: "s:payment_types",
          type: "left",
          as: "ptype",
          on: {
            $eq: ["deal.payment_type_id", "ptype.id"],
          },
        },
        {
          join: "s:delivery_points",
          type: "left",
          as: "point",
          on: {
            $eq: ["deal." + point_field, "point.id"],
          },
        },
        {
          join: "s:cities",
          type: "left",
          as: "city",
          on: {
            $eq: ["point.city_id", "city.id"],
          },
        },
        {
          join: "s:deal_product_containers",
          type: "left",
          as: "dcp",
          on: {
            $eq: ["dcp.deal_id", "deal.id"],
          },
        },
        {
          join: "s:product_containers",
          type: "left",
          as: "pc",
          on: {
            $eq: ["pc.id", "dcp.product_container_id"],
          },
        },
        {
          join: "s:products",
          type: "left",
          as: "p",
          on: {
            $eq: ["p.id", "pc.product_id"],
          },
        },
      ],
      where: {
        $and: [
          { $eq: ["deal.type_id", { $: "d:deals_types:3" }] },
          {
            $in: [
              "delivery_instance_deals.delivery_instance_id",
              {
                $: instanceIds,
              },
            ],
          },
          { $in: ["statuses.code", { $: ["formation", "delivery"] }] },
        ],
      },
    },
    products_return: {
      columns: {
        product_id: "containers.product_id",
        containers: {
          $array_agg: {
            $obj: {
              product_name: "containers.product_name",
              container_name: "containers.name",
              container_count: "containers.count",
              container_weight: "containers.weight",
            },
          },
        },
        container_count_sum: {
          $sum: "containers.count",
        },
        container_weight_sum: {
          $sum: "containers.weight",
        },
      },
      from: {
        columns: {
          product_id: "p.id",
          product_name: "p.name",
          name: "pc.name",
          count: {
            $sum: "dpc.container_quantity",
          },
          weight: {
            $sum: {
              $case: [
                ["pc.price_per_kg", "dpc.container_weight"],
                [
                  {
                    $: true,
                  },
                  {
                    $mul: ["pc.weight", "dpc.container_quantity"],
                  },
                ],
              ],
            },
          },
        },
        from: "s:deal_product_containers",
        from_as: "dpc",
        joins: [
          {
            join: "s:product_containers",
            as: "pc",
            on: {
              $eq: ["pc.id", "dpc.product_container_id"],
            },
          },
          {
            join: "s:products",
            as: "p",
            on: {
              $eq: ["p.id", "pc.product_id"],
            },
          },
        ],
        where: {
          $in: [
            "dpc.deal_id",
            {
              columns: {
                id: "deal.id",
              },
              from: "s:deals",
              from_as: "deal",
              joins: [
                {
                  join: "s:statuses",
                  type: "left",
                  as: "statuses",
                  on: {
                    $eq: ["deal.status_id", "statuses.id"],
                  },
                },
                {
                  join: "s:delivery_instance_deals",
                  as: "delivery_instance_deals",
                  on: {
                    $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                  },
                },
                {
                  join: "s:delivery_points",
                  type: "left",
                  as: "point",
                  on: {
                    $eq: ["deal." + point_field, "point.id"],
                  },
                },
                {
                  join: "s:cities",
                  type: "left",
                  as: "city",
                  on: {
                    $eq: ["point.city_id", "city.id"],
                  },
                },
              ],
              where: {
                $and: [
                  {
                    $in: [
                      "delivery_instance_deals.delivery_instance_id",
                      {
                        $: instanceIds,
                      },
                    ],
                  },
                  { $eq: ["deal.type_id", { $: "d:deals_types:3" }] },
                  { $in: ["statuses.code", { $: ["formation", "delivery"] }] },
                ],
              },
            },
          ],
        },
        groups: ["p.id", "p.name", "pc.id", "pc.name"],
      },
      from_as: "containers",
      groups: ["containers.product_id", "containers.product_name"],
    },
    payments_return: {
      columns: {
        type_name: "pt.name",
        amount: {
          $sum: "dpc.container_price",
        },
      },
      from: "s:deal_product_containers",
      from_as: "dpc",
      joins: [
        {
          join: "s:deals",
          as: "d",
          on: {
            $eq: ["d.id", "dpc.deal_id"],
          },
        },
        {
          join: "s:payment_types",
          as: "pt",
          type: "left",
          on: {
            $eq: ["pt.id", "d.payment_type_id"],
          },
        },
      ],
      where: {
        $in: [
          "dpc.deal_id",
          {
            columns: {
              id: "deal.id",
            },
            from: "s:deals",
            from_as: "deal",
            joins: [
              {
                join: "s:statuses",
                type: "left",
                as: "statuses",
                on: {
                  $eq: ["deal.status_id", "statuses.id"],
                },
              },
              {
                join: "s:delivery_instance_deals",
                as: "delivery_instance_deals",
                on: {
                  $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                },
              },
              {
                join: "s:delivery_points",
                type: "left",
                as: "point",
                on: {
                  $eq: ["deal." + point_field, "point.id"],
                },
              },
              {
                join: "s:cities",
                type: "left",
                as: "city",
                on: {
                  $eq: ["point.city_id", "city.id"],
                },
              },
            ],
            where: {
              $and: [
                {
                  $in: [
                    "delivery_instance_deals.delivery_instance_id",
                    {
                      $: instanceIds,
                    },
                  ],
                },
                { $eq: ["deal.type_id", { $: "d:deals_types:3" }] },
                { $in: ["statuses.code", { $: ["formation", "delivery"] }] },
              ],
            },
          },
        ],
      },
      groups: ["pt.id", "pt.name"],
    },
  };
}

const PRINT_FULL_BOARD_XLSX_MUSTACHE_TEMPLATE =
  '<!DOCTYPE html>\n<table height-scale="0.85" font=Verdana font-size=11 orientation="landscape" area="A:M" scale="60">\n{{#delivery_instances.documents}}\n  <tr>\n  <td></td>\n  <td></td>\n    <td bold>Водитель</td>\n    <td bold>{{driver_name}}</td>\n  </tr>\n  \n  <tr>\n  <td></td>\n  <td></td>\n    <td bold>Дата доставки</td>\n    <td bold>{{#dateDDdMMYdYYYY}}{{delivery_date}}{{/dateDDdMMYdYYYY}}</td>\n  </tr>\n  \n  <tr>\n  <td></td>\n  <td></td>\n    <td>Время погрузки</td>\n    <td>{{loading_time}}</td>\n  </tr>\n\n  <tr>\n  </tr>\n  \n  <tr>\n    <td bold border bgcolor=FF72BF44 width=25>Клиент</td>\n    <td bold border bgcolor=FF72BF44 width=35>Адрес доставки</td>\n    <td bold border bgcolor=FF72BF44 width=35>Комментарий к адресу</td>\n    <td bold border bgcolor=FF72BF44 width=33>Наименование продукта</td>\n    <td bold border bgcolor=FF72BF44 width=15>Тип тары</td>\n    <td bold border bgcolor=FF72BF44 width=15>Количество ведер</td>\n    <td bold border bgcolor=FF72BF44 width=15>Количество кг</td>\n    <td bold border bgcolor=FF72BF44 width=15>Цена за кг, руб</td>\n    <td bold border bgcolor=FF72BF44 width=16>Сумма к оплате, руб</td>\n    <td bold border bgcolor=FF72BF44 width=15>Условия оплаты</td>\n    <td bold border bgcolor=FF72BF44 width=30>Примечания</td>\n  </tr>\n      \n  {{#deals}}\n      {{^first_address}}\n      <tr bgcolor="{{color}}" height="auto" colsColumns="3">\n      {{/first_address}}\n      {{#first_address}}\n      <tr bgcolor="{{color}}" height="auto" colsColumns="0">\n        {{#color}}\n            <td wrap border base bold rowspan="{{counts_address}}" bgcolor="{{color}}">{{client_name}}</td>\n        {{/color}}\n        {{^color}}\n            <td wrap border base rowspan="{{counts_address}}" bgcolor="{{color}}">{{client_name}}</td>\n        {{/color}}\n        <td wrap base border rowspan="{{counts_address}}" bgcolor="{{color}}">{{point_address}}</td>\n        <td wrap border bold base color="770000" rowspan="{{counts_address}}" bgcolor="{{color}}">{{point_comment}}</td>\n      {{/first_address}}\n      {{#not_take}}\n        <td wrap base border>{{product_name}}</td>\n        <td wrap border>{{product_variant_name}}</td>\n        <td wrap border type=number align=center>{{b_quantity}}</td>\n        <td wrap border type=number align=center>{{weight}}</td>\n        <td wrap border type=number align=center>{{#special_price}}{{.}}{{/special_price}}{{^special_price}}{{../b_price}}{{/special_price}}</td>\n        <td wrap border type=number align=center>{{cost}}</td>\n        <td wrap border>{{payment_type_name}}</td>\n        <td wrap border priority bold base color="770000" align="center">{{notes}}</td>\n      {{/not_take}}\n      {{#take}}\n        <td colspan="8" wrap bgcolor="#CCCCCC" border bold base color="770000" align="center">{{notes}}</td>\n      {{/take}}\n    </tr>\n  {{/deals}}\n\n  <tr>\n    <td wrap></td>\n    <td wrap></td>\n    <td wrap></td>\n    <td wrap></td>\n    <td wrap></td>\n    <td bold border wrap type=number align=center>{{b_quantity_sum}}</td>\n    <td bold border wrap type=number align=center>{{weight_sum}}</td>\n    <td></td>\n    <td bold border wrap type=number align=center>{{cost_sum}}</td>\n  </tr>\n\n  <tr>\n  </tr>\n\n  <tr>\n    <td></td>\n    <td></td>\n    <td></td>\n    <td colspan=4 wrap border bold bgcolor=FF72BF44>Продукты</td>\n  </tr>\n\n  <tr>\n    <td></td>\n    <td></td>\n    <td></td>\n    <td wrap border bold bgcolor=FF72BF44>Продукт</td>\n    <td wrap border bold bgcolor=FF72BF44>Тара</td>\n    <td wrap border bold bgcolor=FF72BF44>Всего, шт</td>\n    <td wrap border bold bgcolor=FF72BF44>Всего, кг</td>\n  </tr>\n\n  {{#products}}\n    {{#containers}}\n      <tr height="auto">\n        <td></td>\n        <td></td>\n        <td></td>\n        <td border wrap base>{{product_name}}</td>\n        <td border base>{{container_name}}</td>\n        <td border type=number align=center>{{container_count}}</td>\n        <td border type=number align=center>{{container_weight}}</td>\n      </tr>\n    {{/containers}}\n\n    <tr>\n      <td></td>\n      <td></td>\n      <td></td>\n      <td border></td>\n      <td border></td>\n      <td border bold type=number align=center>{{container_count_sum}}</td>\n      <td border bold type=number align=center>{{container_weight_sum}}</td>\n    </tr>\n\n    <tr>\n        <td></td>\n        <td></td>\n        <td></td>\n        <td border></td>\n        <td border></td>\n        <td border></td>\n        <td border></td>\n    </tr>\n  {{/products}}\n{{/delivery_instances.documents}}\n</body>\n</html>';

/** DocAPI queries for full-board XLSX/PDF (Reportify) and СОХ ExcelJS (`delivery_instances` only). */
function buildPrintFullRoutingReportQueries(
  cityId: string,
  targetDate: string,
  by: string,
): { delivery_instances: any; delivery_instances_return: any } {
  const point_field =
    by == "destination" ? "destination_delivery_point_id" : "delivery_point_id";
  if (process.env.NODE_ENV === "development") {
    console.debug("[DeliverySheetBoard] buildPrintFullRoutingReportQueries", {
      cityId,
      targetDate,
      by,
    });
  }
  return {
    delivery_instances: {
      columns: {
        id: "di.id",
        driver_name: "drivers.name",
        vehicle_number: "vehicles.number",
        delivery_date: "di.delivery_date",
        loading_time: "di.loading_time",
        deals: "deals.deals",
        payments: "payments.payments",
        products: "products.products",
        b_quantity_sum: "deals.b_quantity_sum",
        weight_sum: "deals.weight_sum",
        cost_sum: "deals.cost_sum",
      },
      from: "s:delivery_instances",
      from_as: "di",
      joins: [
        {
          join: "s:employees",
          type: "left",
          as: "drivers",
          on: {
            $eq: ["drivers.id", "di.driver_id"],
          },
        },
        {
          join: "s:delivery_zones",
          as: "dz",
          on: {
            $eq: ["di.delivery_zone_id", "dz.id"],
          },
        },
        {
          join: "s:vehicles",
          type: "left",
          as: "vehicles",
          on: {
            $eq: ["vehicles.id", "di.vehicle_id"],
          },
        },
        {
          join: {
            columns: {
              delivery_instance_id:
                "delivery_instance_deals.delivery_instance_id",
              deals: {
                $array_agg: {
                  $item: {
                    $obj: {
                      id: "deal.id",
                      client_name: "client.name",
                      special_orders: "client.special_orders",
                      expected_delivery_date: "deal.expected_delivery_date",
                      take: { $eq: ["deal.type_id", { $: "d:deals_types:2" }] },
                      not_take: {
                        $not: [
                          { $eq: ["deal.type_id", { $: "d:deals_types:2" }] },
                        ],
                      },
                      confirmed_at: "deal.confirmed_at",
                      point_address: "point.address",
                      point_comment: "point.comment",
                      payment_type_name: "ptype.name",
                      product_name: "p.name",
                      product_pick_up_product: "p.pick_up_product",
                      product_variant_name: "pc.name",
                      b_quantity: "dcp.container_quantity",
                      weight: {
                        $case: [
                          ["pc.price_per_kg", "dcp.container_weight"],
                          [
                            {
                              $: true,
                            },
                            {
                              $mul: ["pc.weight", "dcp.container_quantity"],
                            },
                          ],
                        ],
                      },
                      b_price: "pc.price",
                      special_price: "personal_settings.container_price",
                      cost: "dcp.container_price",
                      notes: "dcp.notes",
                      color: {
                        $case: [
                          [
                            {
                              $in: [
                                "client.type_id",
                                { $: ["d:client_types:6"] },
                              ],
                            },
                            { $: "#CCCCCC" },
                          ],
                          [
                            {
                              $: true,
                            },
                            "client.color",
                          ],
                        ],
                      },
                    },
                  },
                  $order: [
                    {
                      $asc: ["delivery_instance_deals.position"],
                    },
                  ],
                },
              },
              b_quantity_sum: {
                $sum: "dcp.container_quantity",
              },
              weight_sum: {
                $sum: {
                  $case: [
                    ["pc.price_per_kg", "dcp.container_weight"],
                    [
                      {
                        $: true,
                      },
                      {
                        $mul: ["pc.weight", "dcp.container_quantity"],
                      },
                    ],
                  ],
                },
              },
              cost_sum: {
                $sum: "dcp.container_price",
              },
            },
            from: "s:deals",
            from_as: "deal",
            joins: [
              {
                join: "s:statuses",
                type: "left",
                as: "statuses",
                on: {
                  $eq: ["deal.status_id", "statuses.id"],
                },
              },
              {
                join: "s:delivery_instance_deals",
                as: "delivery_instance_deals",
                on: {
                  $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                },
              },
              {
                join: "s:clients",
                type: "left",
                as: "client",
                on: {
                  $eq: ["client.id", "deal.client_id"],
                },
              },
              {
                join: "s:payment_deferment_periods",
                type: "left",
                as: "pdp",
                on: {
                  $eq: ["pdp.id", "client.payment_deferment_period_id"],
                },
              },
              {
                join: "s:payment_types",
                type: "left",
                as: "ptype",
                on: {
                  $eq: ["deal.payment_type_id", "ptype.id"],
                },
              },
              {
                join: "s:delivery_points",
                type: "left",
                as: "point",
                on: {
                  $eq: ["deal." + point_field, "point.id"],
                },
              },
              {
                join: "s:cities",
                type: "left",
                as: "city",
                on: {
                  $eq: ["point.city_id", "city.id"],
                },
              },
              {
                join: "s:warehouses",
                type: "left",
                as: "warehouse",
                on: {
                  $eq: ["deal.delivery_warehouse_id", "warehouse.id"],
                },
              },
              {
                join: "s:deal_product_containers",
                type: "left",
                as: "dcp",
                on: {
                  $eq: ["dcp.deal_id", "deal.id"],
                },
              },
              {
                join: "s:product_containers",
                type: "left",
                as: "pc",
                on: {
                  $eq: ["pc.id", "dcp.product_container_id"],
                },
              },
              {
                join: "s:products",
                type: "left",
                as: "p",
                on: {
                  $eq: ["p.id", "pc.product_id"],
                },
              },
              {
                join: "s:client_product_containers",
                type: "left",
                as: "personal_settings",
                on: {
                  $and: [
                    {
                      $eq: ["personal_settings.product_container_id", "pc.id"],
                    },
                    {
                      $eq: ["personal_settings.client_id", "client.id"],
                    },
                  ],
                },
              },
            ],
            where: {
              $and: [
                { $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:3" }] }] },
                {
                  $in: [
                    "statuses.code",
                    {
                      $: ["formation", "delivery"],
                    },
                  ],
                },
              ],
            },
            groups: ["delivery_instance_deals.delivery_instance_id"],
          },
          type: "left",
          as: "deals",
          on: {
            $eq: ["deals.delivery_instance_id", "di.id"],
          },
        },
        {
          join: {
            columns: {
              delivery_instance_id: "deal.id",
              payments: {
                $array_agg: {
                  $obj: {
                    type_name: "deal.type_name",
                    amount: "deal.amount",
                  },
                },
              },
            },
            from: {
              columns: {
                id: "delivery_instance_deals.delivery_instance_id",
                type_name: "ptype.name",
                amount: {
                  $sum: "dpc.container_price",
                },
              },
              from: "s:deals",
              from_as: "deal",
              joins: [
                {
                  join: "s:deal_product_containers",
                  type: "left",
                  as: "dpc",
                  on: {
                    $eq: ["dpc.deal_id", "deal.id"],
                  },
                },
                {
                  join: "s:statuses",
                  type: "left",
                  as: "statuses",
                  on: {
                    $eq: ["deal.status_id", "statuses.id"],
                  },
                },
                {
                  join: "s:payment_types",
                  type: "left",
                  as: "ptype",
                  on: {
                    $eq: ["deal.payment_type_id", "ptype.id"],
                  },
                },
                {
                  join: "s:delivery_instance_deals",
                  as: "delivery_instance_deals",
                  on: {
                    $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                  },
                },
              ],
              where: {
                $and: [
                  {
                    $not: [{ $eq: ["deal.type_id", { $: "d:deals_types:3" }] }],
                  },
                  {
                    $in: [
                      "statuses.code",
                      {
                        $: ["formation", "delivery"],
                      },
                    ],
                  },
                ],
              },
              groups: [
                "delivery_instance_deals.delivery_instance_id",
                "ptype.name",
              ],
            },
            from_as: "deal",
            groups: ["deal.id"],
          },
          type: "left",
          as: "payments",
          on: {
            $eq: ["payments.delivery_instance_id", "di.id"],
          },
        },
        {
          join: {
            columns: {
              delivery_instance_id: "products.delivery_instance_id",
              products: {
                $array_agg: {
                  $obj: {
                    product_id: "products.product_id",
                    containers: "products.containers",
                    container_count_sum: "products.container_count_sum",
                    container_weight_sum: "products.container_weight_sum",
                  },
                },
              },
            },
            from: {
              columns: {
                delivery_instance_id: "containers.delivery_instance_id",
                product_id: "containers.product_id",
                containers: {
                  $array_agg: {
                    $obj: {
                      product_name: "containers.product_name",
                      container_name: "containers.name",
                      container_count: "containers.count",
                      container_weight: "containers.weight",
                    },
                  },
                },
                container_count_sum: {
                  $sum: "containers.count",
                },
                container_weight_sum: {
                  $sum: "containers.weight",
                },
              },
              from: {
                columns: {
                  delivery_instance_id:
                    "delivery_instance_deals.delivery_instance_id",
                  product_id: "p.id",
                  product_name: "p.name",
                  name: "pc.name",
                  count: {
                    $sum: "dpc.container_quantity",
                  },
                  weight: {
                    $sum: {
                      $mul: ["pc.weight", "dpc.container_quantity"],
                    },
                  },
                },
                from: "s:deals",
                from_as: "deal",
                joins: [
                  {
                    join: "s:statuses",
                    type: "left",
                    as: "statuses",
                    on: {
                      $eq: ["deal.status_id", "statuses.id"],
                    },
                  },
                  {
                    join: "s:delivery_instance_deals",
                    as: "delivery_instance_deals",
                    on: {
                      $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                    },
                  },
                  {
                    join: "s:deal_product_containers",
                    type: "left",
                    as: "dpc",
                    on: {
                      $eq: ["dpc.deal_id", "deal.id"],
                    },
                  },
                  {
                    join: "s:product_containers",
                    type: "left",
                    as: "pc",
                    on: {
                      $eq: ["pc.id", "dpc.product_container_id"],
                    },
                  },
                  {
                    join: "s:products",
                    type: "left",
                    as: "p",
                    on: {
                      $eq: ["p.id", "pc.product_id"],
                    },
                  },
                ],
                where: {
                  $and: [
                    {
                      $not: [
                        { $eq: ["deal.type_id", { $: "d:deals_types:3" }] },
                      ],
                    },
                    {
                      $in: [
                        "statuses.code",
                        {
                          $: ["formation", "delivery"],
                        },
                      ],
                    },
                  ],
                },
                groups: [
                  "delivery_instance_deals.delivery_instance_id",
                  "p.id",
                  "p.name",
                  "pc.id",
                  "pc.name",
                ],
              },
              from_as: "containers",
              groups: [
                "containers.delivery_instance_id",
                "containers.product_id",
                "containers.product_name",
              ],
            },
            from_as: "products",
            groups: ["products.delivery_instance_id"],
          },
          type: "left",
          as: "products",
          on: {
            $eq: ["products.delivery_instance_id", "di.id"],
          },
        },
      ],
      where: {
        $and: [
          {
            $eq: [
              "dz.delivery_zone_group_id",
              {
                $: cityId,
              },
            ],
          },
          {
            $eq: [
              "di.delivery_date",
              {
                $: targetDate,
              },
            ],
          },
        ],
      },
    },
    delivery_instances_return: {
      columns: {
        id: "di.id",
        driver_name: "drivers.name",
        vehicle_number: "vehicles.number",
        delivery_date: "di.delivery_date",
        loading_time: "di.loading_time",
        deals: "deals.deals",
        products: "products.products",
        b_quantity_sum: "deals.b_quantity_sum",
        weight_sum: "deals.weight_sum",
        cost_sum: "deals.cost_sum",
      },
      from: "s:delivery_instances",
      from_as: "di",
      joins: [
        {
          join: "s:employees",
          type: "left",
          as: "drivers",
          on: {
            $eq: ["drivers.id", "di.driver_id"],
          },
        },
        {
          join: "s:delivery_zones",
          as: "dz",
          on: {
            $eq: ["di.delivery_zone_id", "dz.id"],
          },
        },
        {
          join: "s:vehicles",
          type: "left",
          as: "vehicles",
          on: {
            $eq: ["vehicles.id", "di.vehicle_id"],
          },
        },
        {
          join: {
            columns: {
              delivery_instance_id:
                "delivery_instance_deals.delivery_instance_id",
              deals: {
                $array_agg: {
                  $item: {
                    $obj: {
                      id: "deal.id",
                      client_name: "client.name",
                      special_orders: "client.special_orders",
                      expected_delivery_date: "deal.expected_delivery_date",
                      confirmed_at: "deal.confirmed_at",
                      point_address: "point.address",
                      point_comment: "point.comment",
                      payment_type_name: "ptype.name",
                      product_name: "p.name",
                      product_pick_up_product: "p.pick_up_product",
                      product_variant_name: "pc.name",
                      b_quantity: "dcp.container_quantity",
                      weight: {
                        $case: [
                          ["pc.price_per_kg", "dcp.container_weight"],
                          [
                            {
                              $: true,
                            },
                            {
                              $mul: ["pc.weight", "dcp.container_quantity"],
                            },
                          ],
                        ],
                      },
                      b_price: "pc.price",
                      special_price: "personal_settings.container_price",
                      cost: "dcp.container_price",
                      notes: "dcp.notes",
                    },
                  },
                  $order: [
                    {
                      $asc: ["delivery_instance_deals.position"],
                    },
                  ],
                },
              },
              b_quantity_sum: {
                $sum: "dcp.container_quantity",
              },
              weight_sum: {
                $sum: {
                  $case: [
                    ["pc.price_per_kg", "dcp.container_weight"],
                    [
                      {
                        $: true,
                      },
                      {
                        $mul: ["pc.weight", "dcp.container_quantity"],
                      },
                    ],
                  ],
                },
              },
              cost_sum: {
                $sum: "dcp.container_price",
              },
            },
            from: "s:deals",
            from_as: "deal",
            joins: [
              {
                join: "s:statuses",
                type: "left",
                as: "statuses",
                on: {
                  $eq: ["deal.status_id", "statuses.id"],
                },
              },
              {
                join: "s:delivery_instance_deals",
                as: "delivery_instance_deals",
                on: {
                  $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                },
              },
              {
                join: "s:clients",
                type: "left",
                as: "client",
                on: {
                  $eq: ["client.id", "deal.client_id"],
                },
              },
              {
                join: "s:payment_deferment_periods",
                type: "left",
                as: "pdp",
                on: {
                  $eq: ["pdp.id", "client.payment_deferment_period_id"],
                },
              },
              {
                join: "s:payment_types",
                type: "left",
                as: "ptype",
                on: {
                  $eq: ["deal.payment_type_id", "ptype.id"],
                },
              },
              {
                join: "s:delivery_points",
                type: "left",
                as: "point",
                on: {
                  $eq: ["deal." + point_field, "point.id"],
                },
              },
              {
                join: "s:cities",
                type: "left",
                as: "city",
                on: {
                  $eq: ["point.city_id", "city.id"],
                },
              },
              {
                join: "s:warehouses",
                type: "left",
                as: "warehouse",
                on: {
                  $eq: ["deal.delivery_warehouse_id", "warehouse.id"],
                },
              },
              {
                join: "s:deal_product_containers",
                type: "left",
                as: "dcp",
                on: {
                  $eq: ["dcp.deal_id", "deal.id"],
                },
              },
              {
                join: "s:product_containers",
                type: "left",
                as: "pc",
                on: {
                  $eq: ["pc.id", "dcp.product_container_id"],
                },
              },
              {
                join: "s:products",
                type: "left",
                as: "p",
                on: {
                  $eq: ["p.id", "pc.product_id"],
                },
              },
              {
                join: "s:client_product_containers",
                type: "left",
                as: "personal_settings",
                on: {
                  $and: [
                    {
                      $eq: ["personal_settings.product_container_id", "pc.id"],
                    },
                    {
                      $eq: ["personal_settings.client_id", "client.id"],
                    },
                  ],
                },
              },
            ],
            where: {
              $and: [
                { $eq: ["deal.type_id", { $: "d:deals_types:3" }] },
                {
                  $in: [
                    "statuses.code",
                    {
                      $: ["formation", "delivery"],
                    },
                  ],
                },
              ],
            },
            groups: ["delivery_instance_deals.delivery_instance_id"],
          },
          type: "left",
          as: "deals",
          on: {
            $eq: ["deals.delivery_instance_id", "di.id"],
          },
        },
        {
          join: {
            columns: {
              delivery_instance_id: "products.delivery_instance_id",
              products: {
                $array_agg: {
                  $obj: {
                    product_id: "products.product_id",
                    containers: "products.containers",
                    container_count_sum: "products.container_count_sum",
                    container_weight_sum: "products.container_weight_sum",
                  },
                },
              },
            },
            from: {
              columns: {
                delivery_instance_id: "containers.delivery_instance_id",
                product_id: "containers.product_id",
                containers: {
                  $array_agg: {
                    $obj: {
                      product_name: "containers.product_name",
                      container_name: "containers.name",
                      container_count: "containers.count",
                      container_weight: "containers.weight",
                    },
                  },
                },
                container_count_sum: {
                  $sum: "containers.count",
                },
                container_weight_sum: {
                  $sum: "containers.weight",
                },
              },
              from: {
                columns: {
                  delivery_instance_id:
                    "delivery_instance_deals.delivery_instance_id",
                  product_id: "p.id",
                  product_name: "p.name",
                  name: "pc.name",
                  count: {
                    $sum: "dpc.container_quantity",
                  },
                  weight: {
                    $sum: {
                      $mul: ["pc.weight", "dpc.container_quantity"],
                    },
                  },
                },
                from: "s:deals",
                from_as: "deal",
                joins: [
                  {
                    join: "s:statuses",
                    type: "left",
                    as: "statuses",
                    on: {
                      $eq: ["deal.status_id", "statuses.id"],
                    },
                  },
                  {
                    join: "s:delivery_instance_deals",
                    as: "delivery_instance_deals",
                    on: {
                      $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                    },
                  },
                  {
                    join: "s:deal_product_containers",
                    type: "left",
                    as: "dpc",
                    on: {
                      $eq: ["dpc.deal_id", "deal.id"],
                    },
                  },
                  {
                    join: "s:product_containers",
                    type: "left",
                    as: "pc",
                    on: {
                      $eq: ["pc.id", "dpc.product_container_id"],
                    },
                  },
                  {
                    join: "s:products",
                    type: "left",
                    as: "p",
                    on: {
                      $eq: ["p.id", "pc.product_id"],
                    },
                  },
                ],
                where: {
                  $and: [
                    { $eq: ["deal.type_id", { $: "d:deals_types:3" }] },
                    {
                      $in: [
                        "statuses.code",
                        {
                          $: ["formation", "delivery"],
                        },
                      ],
                    },
                  ],
                },
                groups: [
                  "delivery_instance_deals.delivery_instance_id",
                  "p.id",
                  "p.name",
                  "pc.id",
                  "pc.name",
                ],
              },
              from_as: "containers",
              groups: [
                "containers.delivery_instance_id",
                "containers.product_id",
                "containers.product_name",
              ],
            },
            from_as: "products",
            groups: ["products.delivery_instance_id"],
          },
          type: "left",
          as: "products",
          on: {
            $eq: ["products.delivery_instance_id", "di.id"],
          },
        },
      ],
      where: {
        $and: [
          {
            $eq: [
              "dz.delivery_zone_group_id",
              {
                $: cityId,
              },
            ],
          },
          {
            $eq: [
              "di.delivery_date",
              {
                $: targetDate,
              },
            ],
          },
        ],
      },
    },
  };
}

function buildPrintFullXLSXQuery(
  cityId: string,
  targetDate: string,
  by: string,
) {
  const dataQuery: any = {
    template: PRINT_FULL_BOARD_XLSX_MUSTACHE_TEMPLATE,
    queries: buildPrintFullRoutingReportQueries(cityId, targetDate, by),
    format: "xlsx",
  };
  if (process.env.NODE_ENV === "development") {
    console.debug("[DeliverySheetBoard] buildPrintFullXLSXQuery", {
      cityId,
      targetDate,
      by,
      templateLength: PRINT_FULL_BOARD_XLSX_MUSTACHE_TEMPLATE.length,
      headerRowClosedWithTr: PRINT_FULL_BOARD_XLSX_MUSTACHE_TEMPLATE.includes(
        "Примечания</td>\n  </tr>\n",
      ),
    });
  }
  return dataQuery;
}

function buildPrintFullPdfQuery(
  cityId: string,
  targetDate: string,
  by: string,
) {
  const point_field =
    by == "destination" ? "destination_delivery_point_id" : "delivery_point_id";
  const dataQuery: any = {
    template:
      "<!DOCTYPE html>\n<html>\n<head>\n  <meta http-equiv='Content-Type' content='text/html; charset=UTF-8'>\n</head>\n<style>\n  table {\n    border-collapse: collapse;\n  }\n  \n  table, th, td {\n    border: 1px solid black;\n  }\n\n  .no-border {\n    border: none;\n  }\n\n  .pick-up-project td {\n    background: #FFEB3B;\n  }\n  \n  .new-page {\n    page-break-before: always;\n  }\n</style>\n<body>\n  {{#delivery_instances.documents}}\n    <p>Водитель: {{driver_name}}</p>\n    <p>Дата доставки: {{#dateDDdMMYdYYYY}}{{delivery_date}}{{/dateDDdMMYdYYYY}}</p>\n    <p>Номер ТС: {{vehicle_number}}</p>\n    <p>Время погрузки: {{loading_time}}</p>\n    <p>Контактный телефон: 8 913 903 49 30</p>\n\n    <table font=Verdana font-size=8 style='width: 100%;'>\n      <tr height=30 style='text-align: center'>\n        <td style='background: #72BF44'>Клиент</td>\n        <td style='background: #72BF44'>Адрес доставки</td>\n        <td style='background: #72BF44'>Комментарий к адресу</td>\n        <td style='background: #72BF44'>Наименование продукта</td>\n        <td style='background: #72BF44'>Тип тары</td>\n        <td style='background: #72BF44'>Количество ведер</td>\n        <td style='background: #72BF44'>Количество кг</td>\n        <td style='background: #72BF44'>Цена за кг, руб</td>\n        <td style='background: #72BF44'>Сумма к оплате, руб</td>\n        <td style='background: #72BF44'>Условия оплаты</td>\n        <td style='background: #72BF44'>Примечания</td>\n      </tr>\n      \n      {{#deals}}\n        <tr {{#product_pick_up_product}}class='pick-up-project'{{/product_pick_up_product}}>\n          <td>{{client_name}}</td>\n          <td>{{point_address}}</td>\n          <td>{{point_comment}}</td>\n          <td>{{product_name}}</td>\n          <td>{{product_variant_name}}</td>\n          <td style='text-align: center'>{{b_quantity}}</td>\n          <td style='text-align: center'>{{weight}}</td>\n          <td style='text-align: center'>{{#special_price}}{{.}}{{/special_price}}{{^special_price}}{{../b_price}}{{/special_price}}</td>\n          <td style='text-align: center'>{{cost}}</td>\n          <td>{{payment_type_name}}</td>\n          <td>{{notes}}</td>\n        </tr>\n      {{/deals}}\n    </table>\n\n    <h2>Продукты</h2>\n\n    <table font=Verdana font-size=8 style='width: 30%;' class='no-border'>\n      <tr height=30 style='text-align: center'>\n        <td>Продукт</td>\n        <td>Тара</td>\n        <td>Всего, шт</td>\n        <td>Всего, кг</td>\n      </tr>\n      \n      {{#products}}\n        {{#containers}}\n          <tr>\n            <td>{{product_name}}</td>\n            <td>{{container_name}}</td>\n            <td>{{container_count}}</td>\n            <td>{{container_weight}}</td>\n          </tr>\n        {{/containers}}\n\n        <tr class='no-border'>\n          <td colspan='2' class='no-border'>&nbsp;</td>\n          <td>{{container_count_sum}}</td>\n          <td>{{container_weight_sum}}</td>\n        </tr>\n\n        <tr class='no-border'>\n          <td class='no-border' colspan='4'>&nbsp;</td>\n        </tr>\n      {{/products}}\n    </table>\n\n    <p class='new-page'></span>\n  {{/delivery_instances.documents}}\n</body>\n</html>",
    queries: {
      delivery_instances: {
        columns: {
          id: "di.id",
          driver_name: "drivers.name",
          vehicle_number: "vehicles.number",
          delivery_date: "di.delivery_date",
          loading_time: "di.loading_time",
          deals: "deals.deals",
          products: "products.products",
        },
        from: "s:delivery_instances",
        from_as: "di",
        joins: [
          {
            join: "s:employees",
            type: "left",
            as: "drivers",
            on: {
              $eq: ["drivers.id", "di.driver_id"],
            },
          },
          {
            join: "s:delivery_zones",
            as: "dz",
            on: {
              $eq: ["di.delivery_zone_id", "dz.id"],
            },
          },
          {
            join: "s:vehicles",
            type: "left",
            as: "vehicles",
            on: {
              $eq: ["vehicles.id", "di.vehicle_id"],
            },
          },
          {
            join: {
              columns: {
                delivery_instance_id:
                  "delivery_instance_deals.delivery_instance_id",
                deals: {
                  $array_agg: {
                    $item: {
                      $obj: {
                        id: "deal.id",
                        client_name: "client.name",
                        special_orders: "client.special_orders",
                        expected_delivery_date: "deal.expected_delivery_date",
                        confirmed_at: "deal.confirmed_at",
                        point_address: "point.address",
                        point_comment: "point.comment",
                        payment_type_name: "ptype.name",
                        product_name: "p.name",
                        product_pick_up_product: "p.pick_up_product",
                        product_variant_name: "pc.name",
                        b_quantity: "dcp.container_quantity",
                        weight: {
                          $case: [
                            ["pc.price_per_kg", "dcp.container_weight"],
                            [
                              {
                                $: true,
                              },
                              {
                                $mul: ["pc.weight", "dcp.container_quantity"],
                              },
                            ],
                          ],
                        },
                        b_price: "pc.price",
                        special_price: "personal_settings.container_price",
                        cost: "dcp.container_price",
                        notes: "dcp.notes",
                      },
                    },
                    $order: [
                      {
                        $asc: ["delivery_instance_deals.position"],
                      },
                    ],
                  },
                },
              },
              from: "s:deals",
              from_as: "deal",
              joins: [
                {
                  join: "s:statuses",
                  type: "left",
                  as: "statuses",
                  on: {
                    $eq: ["deal.status_id", "statuses.id"],
                  },
                },
                {
                  join: "s:delivery_instance_deals",
                  as: "delivery_instance_deals",
                  on: {
                    $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                  },
                },
                {
                  join: "s:clients",
                  type: "left",
                  as: "client",
                  on: {
                    $eq: ["client.id", "deal.client_id"],
                  },
                },
                {
                  join: "s:payment_deferment_periods",
                  type: "left",
                  as: "pdp",
                  on: {
                    $eq: ["pdp.id", "client.payment_deferment_period_id"],
                  },
                },
                {
                  join: "s:payment_types",
                  type: "left",
                  as: "ptype",
                  on: {
                    $eq: ["deal.payment_type_id", "ptype.id"],
                  },
                },
                {
                  join: "s:delivery_points",
                  type: "left",
                  as: "point",
                  on: {
                    $eq: ["deal." + point_field, "point.id"],
                  },
                },
                {
                  join: "s:cities",
                  type: "left",
                  as: "city",
                  on: {
                    $eq: ["point.city_id", "city.id"],
                  },
                },
                {
                  join: "s:warehouses",
                  type: "left",
                  as: "warehouse",
                  on: {
                    $eq: ["deal.delivery_warehouse_id", "warehouse.id"],
                  },
                },
                {
                  join: "s:deal_product_containers",
                  type: "left",
                  as: "dcp",
                  on: {
                    $eq: ["dcp.deal_id", "deal.id"],
                  },
                },
                {
                  join: "s:product_containers",
                  type: "left",
                  as: "pc",
                  on: {
                    $eq: ["pc.id", "dcp.product_container_id"],
                  },
                },
                {
                  join: "s:products",
                  type: "left",
                  as: "p",
                  on: {
                    $eq: ["p.id", "pc.product_id"],
                  },
                },
                {
                  join: "s:client_product_containers",
                  type: "left",
                  as: "personal_settings",
                  on: {
                    $and: [
                      {
                        $eq: [
                          "personal_settings.product_container_id",
                          "pc.id",
                        ],
                      },
                      {
                        $eq: ["personal_settings.client_id", "client.id"],
                      },
                    ],
                  },
                },
              ],
              where: {
                $in: [
                  "statuses.code",
                  {
                    $: ["formation", "delivery"],
                  },
                ],
              },
              groups: ["delivery_instance_deals.delivery_instance_id"],
            },
            type: "left",
            as: "deals",
            on: {
              $eq: ["deals.delivery_instance_id", "di.id"],
            },
          },
          {
            join: {
              columns: {
                delivery_instance_id: "products.delivery_instance_id",
                products: {
                  $array_agg: {
                    $obj: {
                      product_id: "products.product_id",
                      containers: "products.containers",
                      container_count_sum: "products.container_count_sum",
                      container_weight_sum: "products.container_weight_sum",
                    },
                  },
                },
              },
              from: {
                columns: {
                  delivery_instance_id: "containers.delivery_instance_id",
                  product_id: "containers.product_id",
                  containers: {
                    $array_agg: {
                      $obj: {
                        product_name: "containers.product_name",
                        container_name: "containers.name",
                        container_count: "containers.count",
                        container_weight: "containers.weight",
                      },
                    },
                  },
                  container_count_sum: {
                    $sum: "containers.count",
                  },
                  container_weight_sum: {
                    $sum: "containers.weight",
                  },
                },
                from: {
                  columns: {
                    delivery_instance_id:
                      "delivery_instance_deals.delivery_instance_id",
                    product_id: "p.id",
                    product_name: "p.name",
                    name: "pc.name",
                    count: {
                      $sum: "dpc.container_quantity",
                    },
                    weight: {
                      $sum: {
                        $mul: ["pc.weight", "dpc.container_quantity"],
                      },
                    },
                  },
                  from: "s:deals",
                  from_as: "deal",
                  joins: [
                    {
                      join: "s:statuses",
                      type: "left",
                      as: "statuses",
                      on: {
                        $eq: ["deal.status_id", "statuses.id"],
                      },
                    },
                    {
                      join: "s:delivery_instance_deals",
                      as: "delivery_instance_deals",
                      on: {
                        $eq: ["delivery_instance_deals.deal_id", "deal.id"],
                      },
                    },
                    {
                      join: "s:deal_product_containers",
                      type: "left",
                      as: "dpc",
                      on: {
                        $eq: ["dpc.deal_id", "deal.id"],
                      },
                    },
                    {
                      join: "s:product_containers",
                      type: "left",
                      as: "pc",
                      on: {
                        $eq: ["pc.id", "dpc.product_container_id"],
                      },
                    },
                    {
                      join: "s:products",
                      type: "left",
                      as: "p",
                      on: {
                        $eq: ["p.id", "pc.product_id"],
                      },
                    },
                  ],
                  where: {
                    $in: [
                      "statuses.code",
                      {
                        $: ["formation", "delivery"],
                      },
                    ],
                  },
                  groups: [
                    "delivery_instance_deals.delivery_instance_id",
                    "p.id",
                    "p.name",
                    "pc.id",
                    "pc.name",
                  ],
                },
                from_as: "containers",
                groups: [
                  "containers.delivery_instance_id",
                  "containers.product_id",
                  "containers.product_name",
                ],
              },
              from_as: "products",
              groups: ["products.delivery_instance_id"],
            },
            type: "left",
            as: "products",
            on: {
              $eq: ["products.delivery_instance_id", "di.id"],
            },
          },
        ],
        where: {
          $and: [
            {
              $eq: [
                "dz.delivery_zone_group_id",
                {
                  $: cityId,
                },
              ],
            },
            {
              $eq: [
                "di.delivery_date",
                {
                  $: targetDate,
                },
              ],
            },
          ],
        },
      },
    },
    format: "pdf",
  };

  return dataQuery;
}

function chackValid(state: DeliverySheetBoardState) {
  const unresolvedZone = state.computedZones.find(
    (zone) => zone.id === "__UNRESOLVED__",
  );
  if (
    unresolvedZone &&
    unresolvedZone.targetDeals &&
    unresolvedZone.targetDeals.length === 0
  ) {
    const allRequredData = state.computedZones.reduce((ac, zone) => {
      if (zone.id === "__UNRESOLVED__") {
        return ac;
      } else {
        return ac && zone.employee && zone.vehicle;
      }
    }, true);

    return allRequredData;
  }

  return false;
}

function checkZoneValid(zone: any) {
  let valid = false;
  const totalDealsWeight = zone.targetDeals.reduce((ac: any, deal: any) => {
    return ac + parseFloat(deal.weight || 0);
  }, 0);

  if (zone.employee && zone.vehicle) {
    if (
      totalDealsWeight < parseFloat(zone.vehicle.max_weight) &&
      zone.targetDeals.length < 35
    ) {
      valid = true;
    }
  }

  return { valid, totalDealsWeight };
}

export const actions: ActionTree<DeliverySheetBoardState, RootState> = {
  changeFilterValue({ commit, getters, dispatch, state }, { field, newValue }) {
    (state as any)[field] = newValue;
    if (state.cityId && state.deliveryDate) {
      dispatch("loadDeliverySheetData", {
        cityId: state.cityId,
        deliveryDate: state.deliveryDate,
      });
    }
  },

  setFilterValue({ commit, getters, dispatch, state }, query) {
    if (query.cityId) {
      state.cityId = query.cityId;
    }

    if (query.deliveryDate) {
      // @ts-ignore
      state.deliveryDate = moment(query.deliveryDate).toDate();
    }

    if (query.by) {
      // @ts-ignore
      state.by = query.by;
    }

    if (state.cityId && state.deliveryDate) {
      dispatch("loadDeliverySheetData", {
        cityId: state.cityId,
        deliveryDate: state.deliveryDate,
        by: state.by,
      });
    }
  },

  loadDeliverySheetData(
    { commit, getters, dispatch, state },
    { cityId, deliveryDate, by, silent },
  ) {
    const isSilent = !!silent;

    dsLog("debug", "[DeliverySheetBoard.loadDeliverySheetData] START", {
      silent: isSilent,
      cityId,
      deliveryDate,
      by,
      refreshRequestId: state.refreshRequestId,
    });

    if (isSilent) {
      if (state.dataLoading || state.dataRefreshing) {
        dsLog(
          "debug",
          "[DeliverySheetBoard.loadDeliverySheetData] skip: already loading/refreshing",
          {
            dataLoading: state.dataLoading,
            dataRefreshing: state.dataRefreshing,
          },
        );
        return;
      }

      const dirtyZones = state.computedZones.filter((zone: any) => zone.changed);
      if (dirtyZones.length > 0) {
        dsLog(
          "debug",
          "[DeliverySheetBoard.loadDeliverySheetData] skip: dirty zones",
          {
            dirtyCount: dirtyZones.length,
            dirtyZoneIds: dirtyZones.map((zone: any) => zone.id),
          },
        );
        return;
      }

      commit("START_DATA_REFRESH");
    } else {
      commit("LOAD_DELIVERY_SHEET_DATA", { cityId, deliveryDate });
    }

    const requestId = state.refreshRequestId;
    const targetDeliveryDate = moment(deliveryDate).format("YYYY-MM-DD");
    const point_field =
      by == "destination"
        ? "destination_delivery_point_id"
        : "delivery_point_id";

    const vm = (this as any)._vm;
    vm.$db([
      vm
        .$q(LOAD_DELIVERY_ZONES_DATA_QUERY)
        .select()
        .fileds$((_: any) => {
          return {
            id: "delivery_zones.id",
            group_id: "delivery_zones.delivery_zone_group_id",
            name: "delivery_zones.name",
            instance_id: "delivery_instances.id",
            instance_state: "delivery_instances.state",
            driver_id: "delivery_instances.driver_id",
            vehicle_id: "delivery_instances.vehicle_id",
            loading_time: "delivery_instances.loading_time",
            instance_cost: "delivery_instances.cost",
            reserves: _.array_agg(
              _.obj({
                id: "delivery_instance_product_reserves.id",
                delivery_instance_id:
                  "delivery_instance_product_reserves.delivery_instance_id",
                product_container_id:
                  "delivery_instance_product_reserves.product_container_id",
                container_quantity:
                  "delivery_instance_product_reserves.container_quantity",
                product_id: "product_containers.product_id",
              }),
            ),
          };
        })
        .from("delivery_zones")
        .leftJoin("delivery_instances", (_: any) => {
          return _.and([
            _.eq("delivery_zones.id", "delivery_instances.delivery_zone_id"),
            _.eq(
              "delivery_instances.delivery_date",
              _.const(targetDeliveryDate),
            ),
          ]);
        })
        .leftJoin("delivery_instance_product_reserves", (_: any) => {
          return _.eq(
            "delivery_instance_product_reserves.delivery_instance_id",
            "delivery_instances.id",
          );
        })
        .leftJoin("product_containers", (_: any) => {
          return _.eq(
            "delivery_instance_product_reserves.product_container_id",
            "product_containers.id",
          );
        })
        .where((_: any) => {
          return _.eq("delivery_zones.delivery_zone_group_id", _.const(cityId));
        })
        .group([
          "delivery_zones.id",
          "delivery_zones.name",
          "delivery_instances.id",
          "delivery_instances.state",
          "delivery_instances.driver_id",
          "delivery_instances.vehicle_id",
        ])
        .order((_: any) => {
          return [
            _.asc(["delivery_zones.position"]),
            _.asc(["delivery_zones.name"]),
          ];
        }),

      vm
        .$q(LOAD_TARGET_DEALS_QUERY)
        .raw("documents.list")
        .action({
          columns: {
            id: "deals.id",
            type_id: "deals.type_id",
            weight: "deal_stats.weight", //{ $sum: {$mul: ['pc.weight', 'dcp.container_quantity']} },
            address: "delivery_points.address",
            client_name: "clients.name",
            instance_link_id: "delivery_instance_deals.id",
            instance_id: "delivery_instance_deals.delivery_instance_id",
            available_zones: {
              $array_agg: {
                $obj: {
                  id: "delivery_zones.id",
                  name: "delivery_zones.name",
                },
              },
            },
          },
          from: "s:deals",
          from_as: "deals",

          joins: [
            {
              join: "s:statuses",
              type: "left",
              as: "statuses",
              on: {
                $eq: ["deals.status_id", "statuses.id"],
              },
            },
            {
              join: "s:clients",
              type: "left",
              as: "clients",
              on: {
                $eq: ["deals.client_id", "clients.id"],
              },
            },
            {
              join: "s:delivery_points",
              type: "left",
              as: "delivery_points",
              on: {
                $eq: ["deals." + point_field, "delivery_points.id"],
              },
            },
            {
              join: "s:delivery_points_delivery_zones",
              type: "left",
              as: "zones_link",
              on: {
                $eq: ["delivery_points.id", "zones_link.delivery_point_id"],
              },
            },
            {
              join: "s:delivery_zones",
              type: "left",
              as: "delivery_zones",
              on: {
                $eq: ["delivery_zones.id", "zones_link.delivery_zone_id"],
              },
            },
            {
              join: "s:delivery_instance_deals",
              type: "left",
              as: "delivery_instance_deals",
              on: {
                $eq: ["deals.id", "delivery_instance_deals.deal_id"],
              },
            },
            {
              join: {
                columns: {
                  deal_id: "dpc.deal_id",
                  // sapmle_count: { $count: 'dpc.id' },
                  weight: {
                    $sum: { $mul: ["pc.weight", "dpc.container_quantity"] },
                  },
                },
                from: "s:deal_product_containers",
                from_as: "dpc",
                joins: [
                  {
                    join: "s:product_containers",
                    type: "left",
                    as: "pc",
                    on: {
                      $eq: ["pc.id", "dpc.product_container_id"],
                    },
                  },
                ],
                groups: ["dpc.deal_id"],
                // where: {
                //   $or: [
                //     { $eq: ['dpc.is_sample', { $: true } ] },
                //     { $eq: ['pc.is_sample', { $: true } ] },
                //   ]
                // }
              },
              type: "left",
              as: "deal_stats",
              on: { $eq: ["deal_stats.deal_id", "deals.id"] },
            },
          ],

          where: {
            ...(moment(targetDeliveryDate).isSameOrBefore(
              moment()
                .set("hour", 9)
                .set("minute", 0),
              "minutes",
            )
              ? {
                  $and: [
                    {
                      $eq: [
                        "deals.expected_delivery_date",
                        { $: targetDeliveryDate },
                      ],
                    },
                    {
                      $eq: [
                        "delivery_zones.delivery_zone_group_id",
                        { $: cityId },
                      ],
                    },
                    {
                      $in: ["statuses.code", { $: ["formation", "delivery"] }],
                    },
                    // { $or: [
                    //   { $null: [ 'statuses.final' ] },
                    //   { $eq: [ 'statuses.final', { $: 'success'}]}
                    // ] } // not sure
                    // { $not: [ { $null: [`delivery_instance_deals.id`] } ] }
                  ],
                }
              : {
                  $and: [
                    {
                      $eq: [
                        "deals.expected_delivery_date",
                        { $: targetDeliveryDate },
                      ],
                    },
                    {
                      $eq: [
                        "delivery_zones.delivery_zone_group_id",
                        { $: cityId },
                      ],
                    },
                    {
                      $in: ["statuses.code", { $: ["formation", "delivery"] }],
                    },
                  ],
                }),
          },
          groups: [
            "deals.id",
            "delivery_points.address",
            "clients.name",
            "delivery_instance_deals.id",
            "delivery_instance_deals.delivery_instance_id",
            "deal_stats.weight",
          ],
          order: [{ $asc: ["delivery_points.address"] }],
          page_size: 1000,
        }),

      vm
        .$q(LOAD_VEHICLES_QUERY)
        .raw("documents.list")
        .action({
          columns: {
            id: "vehicles.id",
            number: "vehicles.number",
            model: "vehicles.model",
            max_weight: "vehicles.max_weight",
            archived_at: "vehicles.archived_at",
            available_employees: {
              $array_agg: {
                $obj: {
                  id: "employees.id",
                  name: "employees.name",
                  archived_at: "employee_vehicles.archived_at",
                },
              },
            },
          },
          from: "s:vehicles",
          from_as: "vehicles",

          joins: [
            {
              join: "s:employee_vehicles",
              type: "left",
              as: "employee_vehicles",
              on: {
                $and: [
                  { $eq: ["employee_vehicles.vehicle_id", "vehicles.id"] },
                ],
              },
            },
            {
              join: "s:employees",
              type: "left",
              as: "employees",
              on: {
                $eq: ["employees.id", "employee_vehicles.employee_id"],
              },
            },
          ],

          where: {
            $and: [
              {
                $or: [
                  { $null: ["vehicles.city_id"] },
                  {
                    $in: [
                      "vehicles.city_id",
                      {
                        columns: {
                          id: "delivery_zones.city_id",
                        },
                        from: "s:delivery_zones",
                        from_as: "delivery_zones",
                        where: {
                          $eq: [
                            "delivery_zones.delivery_zone_group_id",
                            { $: cityId },
                          ],
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          groups: ["vehicles.id", "vehicles.number", "vehicles.model"],
          order: [{ $asc: ["vehicles.model"] }],
          page_size: 1000,
        }),

      vm
        .$q(PL_PRODUCTS_LIST)
        .raw("documents.list")
        .action({
          columns: {
            id: "product.id",
            name: "product.name",
            code: "product.code",
            position: "product.position",
          },
          from: "s:products",
          from_as: "product",
          order: [{ $asc: ["product.name"] }],
          page_size: 1000,
        }),

      vm
        .$q(PL_PRODUCT_VARIANTS_LIST)
        .raw("documents.list")
        .action({
          columns: {
            id: "product_container.id",
            product_id: "product_container.product_id",
            name: "product_container.name",
            position: "product_container.position",
            weight: "product_container.weight",
            volume: "product_container.volume",
            price: "product_container.price",
          },

          from: "s:product_containers",
          from_as: "product_container",
          order: [{ $asc: ["product_container.name"] }],
          page_size: 1000,
        }),
    ]).then((result: ServerResult) => {
      if (state.refreshRequestId !== requestId) {
        dsLog(
          "debug",
          "[DeliverySheetBoard.loadDeliverySheetData] stale response ignored",
          {
            requestId,
            currentRefreshRequestId: state.refreshRequestId,
            silent: isSilent,
          },
        );
        return;
      }

      const zones = result
        .getOperation(LOAD_DELIVERY_ZONES_DATA_QUERY)
        .getContent().documents;
      const targetDeals = result
        .getOperation(LOAD_TARGET_DEALS_QUERY)
        .getContent().documents;

      commit("DELIVERY_SHEET_LOADED_SUCCESS", {
        zones,
        targetDeals,
        vehicles: result
          .getOperation(LOAD_VEHICLES_QUERY)
          .getContent()
          .documents.filter((item: any) => {
            return (
              !item.archived_at ||
              moment(item.archived_at).isAfter(moment(targetDeliveryDate))
            );
          }),

        products: result.getOperation(PL_PRODUCTS_LIST).getContent().documents,
        variants: result.getOperation(PL_PRODUCT_VARIANTS_LIST).getContent()
          .documents,
      });

      if (isSilent) {
        commit("FINISH_DATA_REFRESH");
        dsLog(
          "info",
          "[DeliverySheetBoard.loadDeliverySheetData] silent refresh success",
          {
            zonesCount: zones.length,
            dealsCount: targetDeals.length,
            cityId,
            deliveryDate: targetDeliveryDate,
            requestId,
          },
        );
      }
    }).catch((error: any) => {
      dsLog(
        isSilent ? "warn" : "error",
        "[DeliverySheetBoard.loadDeliverySheetData] ERROR",
        {
          silent: isSilent,
          cityId,
          deliveryDate: targetDeliveryDate,
          by,
          requestId,
          message: error && error.message,
          stack: error && error.stack,
        },
      );

      if (state.refreshRequestId !== requestId) {
        dsLog(
          "debug",
          "[DeliverySheetBoard.loadDeliverySheetData] error for stale request ignored",
          { requestId, currentRefreshRequestId: state.refreshRequestId },
        );
        return;
      }

      if (isSilent) {
        commit("FINISH_DATA_REFRESH");
      } else {
        commit("DELIVERY_SHEET_LOADED_ERROR");
      }
    });
  },

  setVehicle({ commit, getters, dispatch, state }, { zone, vehicle }) {
    commit("SET_VEHICLE", { zone, vehicle });
  },

  setEmployee({ commit, getters, dispatch, state }, { zone, employee }) {
    commit("SET_EMPLOYEE", { zone, employee });
  },

  setTime({ commit, getters, dispatch, state }, { zone, time }) {
    console.log("time", time);
    commit("SET_TIME", { zone, time });
  },

  moveDealBetweenZone(
    { commit, getters, dispatch, state },
    { zone, newItemList },
  ) {
    commit("MOVE_DEAL_BETWEEN_ZONE", { zone, newItemList });
  },

  saveZone({ commit, getters, dispatch, state }, { zone }) {
    const vm = (this as any)._vm;

    let loadingTime: any = null;

    if (zone.loading_time) {
      loadingTime = moment(zone.loading_time, "HH:mm:ss").format("HH:mm");
      loadingTime = `${loadingTime}:00`;
    }

    const instance = {
      id: zone.instance_id,
      delivery_zone_id: zone.id,
      driver_id: zone.employee.id,
      delivery_date: moment(state.deliveryDate as string).format("YYYY-MM-DD"),
      vehicle_id: zone.vehicle.id,
      loading_time: loadingTime,
      state: "init",
    };

    const queries = [
      instance.id
        ? // db.update(`SAVE_ZONE`, instance.id, instance)
          vm
            .$q(`SAVE_ZONE`)
            .update(instance.id, instance)
            .createManyRelation(
              "delivery_instance_deals",
              "delivery_instance_id",
              (createRelated: any) => {
                return zone.targetDeals
                  .filter((deal: any) => !deal.instance_link_id)
                  .map((deal: any, index: number) =>
                    createRelated({
                      deal_id: deal.id,
                      position: index,
                    }),
                  );
              },
            )
            .updateManyRelation(
              "delivery_instance_deals",
              "delivery_instance_id",
              (updateRelated: any) => {
                return zone.targetDeals
                  .filter((deal: any) => deal.instance_link_id)
                  .map((deal: any, index: number) =>
                    updateRelated({
                      id: deal.instance_link_id,
                      deal_id: deal.id,
                      position: index,
                    }),
                  );
              },
            )
            .deleteManyRelation(
              "delivery_instance_deals",
              "delivery_instance_id",
              (deleteRelated: any) => {
                return zone.forDeleteDeals
                  .map((deal: any, index: number) =>
                    deleteRelated({
                      id: deal.instance_link_id,
                    }),
                  )
                  .filter((deal: any) => deal);
              },
            )
        : vm
            .$q(`SAVE_ZONE`)
            .create("delivery_instances", instance)
            .withManyRelation(
              "delivery_instance_deals",
              "delivery_instance_id",
              (createRelated: any) => {
                return zone.targetDeals.map((deal: any, index: number) =>
                  createRelated({
                    deal_id: deal.id,
                    position: index,
                  }),
                );
              },
            ),
    ];

    return vm.$db(queries).then((result: ServerResult) => {
      commit(
        "ZONE_SAVING_SUCCESS",
        result.getOperation(`SAVE_ZONE`).getContent(),
      );
      // resolve();
    });
  },

  confirmZone({ commit, getters, dispatch, state }, { zone }) {
    const vm = (this as any)._vm;

    const instance = {
      id: zone.instance_id,
      state: "confirmed",
    };
    const queries = [vm.$q(`SAVE_ZONE`).update(instance.id, instance)];
    // console.log(zone.targetDeals);
    // zone.targetDeals.map((deal: any) => {
    //   if (deal.type_id == 'd:deals_types:4' || (deal.type_id == 'd:deals_types:3' && ['d:client_types:5', 'd:client_types:9'].includes(deal.client_type_id))) {
    //     queries.push(
    //       vm.$q('deals_delivery_approve-' + deal.id)
    //         .raw('deals_delivery_approve')
    //         .action({
    //             deal_id: deal.id,
    //             is_approved: false,
    //             increse: false
    //         }
    //       )
    //     )
    //   }
    // });

    return vm.$db(queries).then((result: ServerResult) => {
      commit(
        "ZONE_CONFIRM_SUCCESS",
        result.getOperation(`SAVE_ZONE`).getContent(),
      );
      // resolve();
    });
  },

  setInstanceCost({ commit, getters, dispatch, state }, { zone, value }) {
    const vm = (this as any)._vm;

    const instance = {
      id: zone.instance_id,
      cost: value,
    };

    return vm
      .$db([vm.$q(`SAVE_ZONE`).update(instance.id, instance)])
      .then((result: ServerResult) => {
        commit(
          "SET_COST_SUCCESS",
          result.getOperation(`SAVE_ZONE`).getContent(),
        );
        // resolve();
      });
  },

  updateReserves({ state }, { zone, reserves }) {
    const zoneIndex = state.computedZones.findIndex(
      (_zone: any) => _zone.instance_id === zone.instance_id,
    );
    state.computedZones[zoneIndex].reserves = reserves;
  },

  // DOCUMENTS DOWNLOAD
  print({ state }, instanceIds) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q(ROUTING_SHEET_DATA)
          .raw("delivery_instances.routing_sheet_data")
          .action({
            queries: buildRoutingSheetPrintQueries(instanceIds, state.by),
          }),
      ])
        .then(async (result: ServerResult) => {
          try {
            const content = result
              .getOperation(ROUTING_SHEET_DATA)
              .getContent() as RoutingSheetBatchPayload;
            if (process.env.NODE_ENV === "development") {
              console.debug(
                "[DeliverySheetBoard.print] routing_sheet_data keys",
                content && Object.keys(content),
              );
            }
            const buffer = await buildRoutingSheetWorkbookBuffer(content);
            const blob = new Blob([buffer], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(blob, "export.xlsx");
            resolve(undefined);
          } catch (e) {
            console.error("[DeliverySheetBoard.print] excel build failed", e);
            reject(e);
          }
        })
        .catch(reject);
    });
  },

  printFullXLSX({ rootState, state }, instanceIds) {
    const vm = (this as any)._vm;

    if (process.env.NODE_ENV === "development") {
      console.debug("[DeliverySheetBoard.printFullXLSX] START", {
        cityId: state.cityId,
        deliveryDate: state.deliveryDate,
        by: state.by,
        instanceIdsCount: Array.isArray(instanceIds) ? instanceIds.length : 0,
        templateLength: PRINT_FULL_BOARD_XLSX_MUSTACHE_TEMPLATE.length,
      });
    }

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.report")
          .action(
            buildPrintFullXLSXQuery(
              state.cityId as string,
              moment(state.deliveryDate as string).format("YYYY-MM-DD"),
              state.by,
            ),
          ),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        if (process.env.NODE_ENV === "development") {
          console.debug("[DeliverySheetBoard.printFullXLSX] DONE", {
            hasToken: !!fToken,
          });
        }

        resolve(undefined);
      }).catch((error: any) => {
        console.error("[DeliverySheetBoard.printFullXLSX] ERROR", {
          message: error && error.message,
        });
        reject(error);
      });
    });
  },

  printFullPDF({ rootState, state }, instanceIds) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.report")
          .action(
            buildPrintFullPdfQuery(
              state.cityId as string,
              moment(state.deliveryDate as string).format("YYYY-MM-DD"),
              state.by,
            ),
          ),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.pdf");

        resolve(undefined);
      });
    });
  },

  printPDF({ rootState, state }, instanceIds) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("delivery_instances.export")
          .action({
            delivery_zone_group_id: state.cityId,
            delivery_date: moment(state.deliveryDate as string).format(
              "YYYY-MM-DD",
            ),
          }),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.pdf");

        resolve(undefined);
      });
    });
  },
};

export const mutations: MutationTree<DeliverySheetBoardState> = {
  LOAD_FILTER_DATA: (state) => {
    state.filterDataLoading = true;
  },

  FILTER_DATA_LOADED_SUCCESS: (state, { cities }) => {
    state.cities = cities;
    state.filterDataLoading = false;
    state.filterDataLoaded = true;
  },

  LOAD_DELIVERY_SHEET_DATA: (state, { cityId, deliveryDate }) => {
    state.cityId = cityId;
    state.deliveryDate = deliveryDate;
    state.dataRequested = true;
    state.dataLoading = true;
    state.dataRefreshing = false;
    state.refreshRequestId += 1;
  },

  START_DATA_REFRESH: (state) => {
    state.dataRefreshing = true;
    state.refreshRequestId += 1;
  },

  FINISH_DATA_REFRESH: (state) => {
    state.dataRefreshing = false;
  },

  DELIVERY_SHEET_LOADED_ERROR: (state) => {
    state.dataLoading = false;
  },

  DELIVERY_SHEET_LOADED_SUCCESS: (
    state,
    { zones, targetDeals, vehicles, products, variants },
  ) => {
    const zonesById: any = {};
    const zonesByInstanceId: any = {};
    const zonesIds: any[] = [];

    for (const zone of zones) {
      if (zone.vehicle_id) {
        zone.vehicle = vehicles.find(
          (vehicle: any) => vehicle.id === zone.vehicle_id,
        );
      }

      if (zone.driver_id && zone.vehicle && zone.vehicle.available_employees) {
        zone.employee = zone.vehicle.available_employees.find(
          (employee: any) => employee.id === zone.driver_id,
        );
      }

      zonesById[zone.id] = {
        ...zone,
        loading_time: zone.loading_time
          ? moment(zone.loading_time, "HH:mm:ss.000000").toDate()
          : moment()
              .minutes(0)
              .hours(9)
              .toDate(),
        changed: false,
        targetDeals: [],
        forDeleteDeals: [],
      };

      if (zone.instance_id) {
        zonesByInstanceId[zone.instance_id] = zonesById[zone.id];
      }

      zonesIds.push(zone.id);
    }

    const freeZone = [];
    for (const deal of targetDeals) {
      if (deal.instance_id && zonesByInstanceId[deal.instance_id]) {
        zonesByInstanceId[deal.instance_id].targetDeals.push(deal);
      } else {
        const avZoneIds = deal.available_zones.map((zone: any) => zone.id);
        let addet = false;

        for (const zoneId of avZoneIds) {
          if (zoneId && zonesById[zoneId].targetDeals.length < 35) {
            zonesById[zoneId].targetDeals.push(deal);
            addet = true;

            zonesById[zoneId].changed = true;
            zonesById[zoneId].instance_state = "init";
            break;
          }
        }

        if (!addet) {
          freeZone.push(deal);
        }
      }
    }

    for (const key in zonesById) {
      zonesById[key] = { ...zonesById[key], ...checkZoneValid(zonesById[key]) };
    }

    state.computedZones = [
      // { id: '__UNRESOLVED__', name: 'Нераспределенные', targetDeals: freeZone},
      ...Object.values(zonesById),
    ];
    state.zones = zones;
    state.vehicles = vehicles.filter(
      (vehicle: any) =>
        vehicle.available_employees.filter((empl: any) => empl.id).length > 0,
    );
    state.products = products;
    state.variants = variants;
    //   @ts-ignore
    state.targetDeals = targetDeals;
    state.dataLoading = false;
    state.dataLoaded = true;
  },

  MOVE_DEAL_BETWEEN_ZONE: (state, { zone, newItemList }) => {
    const computedZones = cloneDeep(state.computedZones);

    // const zoneIndex = computedZones.findIndex((_zone: any) => _zone.id === zone.id);
    const zoneIndex = findIndex(computedZones, { id: zone.id });

    const removedIds = difference(
      computedZones[zoneIndex].targetDeals.map((deal: any) => deal.id),
      newItemList.map((deal: any) => deal.id),
    );

    const addedIds = difference(
      newItemList.map((deal: any) => deal.id),
      computedZones[zoneIndex].targetDeals.map((deal: any) => deal.id),
    );

    console.log("removedIds", removedIds, "addedIds", addedIds);

    for (const dealId of removedIds) {
      const dealIndex = findIndex(computedZones[zoneIndex].targetDeals, {
        id: dealId,
      });

      computedZones[zoneIndex].forDeleteDeals.splice(
        0,
        0,
        cloneDeep(computedZones[zoneIndex].targetDeals[dealIndex]),
      );
    }

    for (const dealId of addedIds) {
      const dealIndex = findIndex(newItemList, { id: dealId });

      delete newItemList[dealIndex].instance_link_id;
    }

    computedZones[zoneIndex].targetDeals = newItemList;

    computedZones[zoneIndex] = {
      ...computedZones[zoneIndex],
      changed: true,
      instance_state: "init",
      ...checkZoneValid(computedZones[zoneIndex]),
    };

    state.computedZones = computedZones;
    state.valid = chackValid({ ...state, computedZones });
  },

  SET_VEHICLE: (state, { zone, vehicle }) => {
    const computedZones = cloneDeep(state.computedZones);

    const zoneIndex = computedZones.findIndex(
      (_zone: any) => _zone.id === zone.id,
    );
    computedZones[zoneIndex].vehicle = vehicle;
    computedZones[zoneIndex] = {
      ...computedZones[zoneIndex],
      changed: true,
      ...checkZoneValid(computedZones[zoneIndex]),
    };

    state.computedZones = computedZones;
    state.valid = chackValid({ ...state, computedZones });
  },

  SET_EMPLOYEE: (state, { zone, employee }) => {
    const computedZones = cloneDeep(state.computedZones);

    const zoneIndex = computedZones.findIndex(
      (_zone: any) => _zone.id === zone.id,
    );
    computedZones[zoneIndex].employee = employee;
    computedZones[zoneIndex] = {
      ...computedZones[zoneIndex],
      changed: true,
      ...checkZoneValid(computedZones[zoneIndex]),
    };

    state.computedZones = computedZones;
    state.valid = chackValid({ ...state, computedZones });
  },

  SET_TIME: (state, { zone, time }) => {
    const computedZones = cloneDeep(state.computedZones);
    // moment(zone.loading_time, 'HH:mm:ss.000000').toDate()
    const zoneIndex = computedZones.findIndex(
      (_zone: any) => _zone.id === zone.id,
    );
    computedZones[zoneIndex].loading_time = time;
    computedZones[zoneIndex] = {
      ...computedZones[zoneIndex],
      changed: true,
      ...checkZoneValid(computedZones[zoneIndex]),
    };

    state.computedZones = computedZones;
    state.valid = chackValid({ ...state, computedZones });
  },

  SAVE_ZONE: (state, { zone }) => {
    const computedZones = cloneDeep(state.computedZones);

    const zoneIndex = computedZones.findIndex(
      (_zone: any) => _zone.id === zone.id,
    );
    computedZones[zoneIndex].saving = true;

    state.computedZones = computedZones;
    state.valid = chackValid({ ...state, computedZones });
  },

  ZONE_SAVING_SUCCESS: (
    state,
    { document, create_related, update_related },
  ) => {
    const computedZones = cloneDeep(state.computedZones);

    console.log(document, create_related, update_related);

    const zoneIndex = computedZones.findIndex(
      (_zone: any) => _zone.id === document.delivery_zone_id,
    );
    computedZones[zoneIndex] = {
      ...computedZones[zoneIndex],
      saving: false,
      changed: false,
      instance_id: document.id,
      instance_state: document.state,
      ...(create_related &&
      create_related["s:delivery_instance_deals/delivery_instance_id"]
        ? {
            targetDeals: computedZones[zoneIndex].targetDeals.map(
              (deal: any) => {
                const relatedObj = create_related[
                  "s:delivery_instance_deals/delivery_instance_id"
                ].documents.find(
                  (relatetResult: any) => relatetResult.deal_id === deal.id,
                );

                if (relatedObj) {
                  return {
                    ...deal,
                    instance_link_id: relatedObj.id,
                    instance_id: relatedObj.delivery_instance_id,
                  };
                } else {
                  return deal;
                }
              },
            ),
          }
        : {}),
      forDeleteDeals: [],
    };

    state.computedZones = computedZones;
    state.valid = chackValid({ ...state, computedZones });
  },

  ZONE_CONFIRM_SUCCESS: (state, { document, related }) => {
    const computedZones = cloneDeep(state.computedZones);

    const zoneIndex = computedZones.findIndex(
      (_zone: any) => _zone.id === document.delivery_zone_id,
    );
    computedZones[zoneIndex] = {
      ...computedZones[zoneIndex],
      saving: false,
      changed: false,
      instance_state: document.state,
    };

    state.computedZones = computedZones;
    state.valid = chackValid({ ...state, computedZones });
  },

  SET_COST_SUCCESS: (state, { document, related }) => {
    const computedZones = cloneDeep(state.computedZones);

    console.log(document);

    const zoneIndex = computedZones.findIndex(
      (_zone: any) => _zone.id === document.delivery_zone_id,
    );
    computedZones[zoneIndex] = {
      ...computedZones[zoneIndex],
      instance_cost: document.cost,
    };

    state.computedZones = computedZones;
    state.valid = chackValid({ ...state, computedZones });
  },

  CREATE_RESERVE_SUCCESS: (state, { document }) => {
    const computedZones = cloneDeep(state.computedZones);

    const zoneIndex = computedZones.findIndex(
      (_zone: any) => _zone.instance_id === document.delivery_instance_id,
    );
    computedZones[zoneIndex].reserves.push(document);

    state.computedZones = computedZones;
  },

  // UPDATE_RESERVE_SUCCESS: (state, {document}) => {
  //   const computedZones = cloneDeep(state.computedZones);

  //   const zoneIndex = computedZones.findIndex((_zone: any) => _zone.instance_id === document.delivery_instance_id);
  //   const reserveIndex = computedZones[zoneIndex].reserves.findIndex((reserve: any) => reserve.id === document.id)

  //    computedZones[zoneIndex].reserves[reserveIndex] = document;

  //    state.computedZones = computedZones;
  // },

  // DELETE_RESERVE_SUCCESS: (state, {document}) => {
  //   const computedZones = cloneDeep(state.computedZones);

  //   const zoneIndex = computedZones.findIndex((_zone: any) => _zone.instance_id === document.delivery_instance_id);
  //   computedZones[zoneIndex].reserves = computedZones[zoneIndex].reserves.filter((reserve: any) => reserve.id !== document.id);

  //   state.computedZones = computedZones;
  // },
};

const getters: GetterTree<DeliverySheetBoardState, RootState> = {
  allInstanceIds: (state: any) => {
    return state.computedZones
      .filter((zone: any) => zone.instance_id)
      .map((zone: any) => zone.instance_id);
  },
  countDeliverySheetInit: (state: any) => {
    return state.computedZones.filter(
      (zone: any) => zone.instance_state == "init",
    ).length;
  },

  // export const selectFeature = createFeatureSelector<AppState, DeliverySheetState>('deliverySheet');

  // export const selectFilterLoding = createSelector(selectFeature, (state: DeliverySheetState) => state.filterDataLoading);
  // export const selectFilterLoded = createSelector(selectFeature, (state: DeliverySheetState) => state.filterDataLoaded);
  // export const selectFilterCities = createSelector(selectFeature, (state: DeliverySheetState) => state.cities);

  // export const selectIsDataRequested = createSelector(selectFeature, (state: DeliverySheetState) => state.dataRequested);
  // export const selectDataLoding = createSelector(selectFeature, (state: DeliverySheetState) => state.dataLoading);
  // export const selectDataLoded = createSelector(selectFeature, (state: DeliverySheetState) => state.dataLoaded);

  // export const selectZones = createSelector(selectFeature, (state: DeliverySheetState) => state.zones);
  // export const selectComputedZones = createSelector(selectFeature, (state: DeliverySheetState) => state.computedZones);
  // export const selectVehicles = createSelector(selectFeature, (state: DeliverySheetState) => state.vehicles);
  // export const selectValid = createSelector(selectFeature, (state: DeliverySheetState) => state.valid);

  // export const selectProducts = createSelector(selectFeature, (state: DeliverySheetState) => state.products);
  // export const selectVariants = createSelector(selectFeature, (state: DeliverySheetState) => state.variants);
};

export const STORE_KEY = "$_delivery_sheet_board";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
